import { useQuery } from '@tanstack/react-query';

import type { OrganizationSubscription } from '~/interfaces/subscriber_preferences';

export const organizationSubscriptionsKeys = {
  all: ['organization-subscriptions'] as const,
  bySubscriber: (subscriberId: string) => ['organization-subscriptions', subscriberId] as const,
};

const fetchOrganizationSubscriptions = async (subscriberId: string): Promise<OrganizationSubscription[] | null> => {
  const response = await fetch(`/organization_subscriptions?subscriber_id=${subscriberId}`);
  if (!response.ok) throw new Error('Failed to fetch organization subscriptions');
  return response.json();
};

interface Props {
  enabled: boolean;
  subscriberId: string;
}

interface ReturnProps {
  isLoading: boolean;
  data?: OrganizationSubscription[] | null;
  enabled?: boolean;
  refetch: () => void;
}

const useStaticOrganizationSubscriptions = ({ enabled, subscriberId }: Props): ReturnProps => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: organizationSubscriptionsKeys.bySubscriber(subscriberId),
    queryFn: () => fetchOrganizationSubscriptions(subscriberId),
    enabled: enabled && !!subscriberId,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  return {
    isLoading: isLoading && enabled,
    data: data ?? null,
    refetch,
  };
};

export default useStaticOrganizationSubscriptions;
